import {action, computed, observable, runInAction} from 'mobx';
import {TechseeBaseController} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-controller';
import {LOG_EVENTS} from '@techsee/techsee-common/lib/constants/event-logs.constants';
import {VideoLayoutType} from '@techsee/techsee-common/lib/constants/media.constants';
import {SessionClientRole} from '@techsee/techsee-media-service/lib/MediaConstants';
import {
    PinningId,
    OnPinnedUserChangedCb,
    IMultipartyController,
    MultipartyEvents,
    OnSnapshotRequestedCb,
    MultipartyConnectFailedEvent,
    MultipartyConnectionCreatedEvent,
    MultipartyConnectionDestroyedEvent,
    MultipartyMainVideoMirrorStateChangedEvent,
    MultipartySnapshotAllowedChangedEvent,
    MultipartyStreamCreatedEvent,
    MultipartyStreamDestroyedEvent,
    MultipartyStreamPropertyChangedEvent,
    MultipartyMainVideoPinnedAndStreamingStateChangedEvent,
    MultipartyAudioStateEvent,
    MultipartyScreenShareStateChangedEvent
} from '@techsee/techsee-nway-media-service-client/';
import {toSessionClientRoleFromId} from '@techsee/techsee-nway-media-service-client/lib/services/types';
import {IRoomService} from '@techsee/techsee-client-infra/lib/services/RoomService';
import {IRoom} from '../../../../../models/Room';
import {getRootStore} from '../../../../../app.bootstrap';
import {MagicMarkerController} from './MagicMarkerController';
import {IChatApi} from '../../../../../services/ts-chat-api/ChatApi';

export type NwayParticipant = {
    participantId: string;
    firstName: string;
    lastName: string;
    connected: boolean;
};

export interface IVideoParticipantContext {
    readonly nwayParticipants: NwayParticipant[];
    readonly isAgent: boolean;
    readonly allowRemoveParticipant: boolean;
}

export const VideoControllerEvents = {
    removeParticipant: 'removeParticipant'
} as const;

export interface IVideoController {
    multipartyController?: IMultipartyController;
    isPeerMediaPermissionRejected: boolean;
    canTakeSnapshot: boolean;
    videoLayoutType: VideoLayoutType;
    translate: (token: string, data?: Record<string, unknown>) => string;
    allowMagicMarker: boolean;
    showRemoveModal: boolean;
    connectedParticipants: NwayParticipant[];
    removeConfirmMessage: string;
    confirmBtnText: string;
    cancelBtnText: string;
    setMultipartyController(multipartyController: IMultipartyController): void;
    setIsPeerMediaPermissionsRejected(isClientMediaPermissionRejected: boolean): void;
    setVideoLayoutType(layoutType: VideoLayoutType): void;
    onRemoveParticipantRequested(userId: string): void;
    canRemoveParticipant: boolean;
    confirmRemoveParticipant(): void;
    cancelRemoveParticipant(): void;
    onPinnedUserChangeRequested: OnPinnedUserChangedCb;
    onSnapshotRequested?: OnSnapshotRequestedCb;
    takeSnapshot(): string | null;
    magicMarkerController: MagicMarkerController;
}

export class VideoController extends TechseeBaseController implements IVideoController {
    @observable private _multipartyController?: IMultipartyController;
    @observable private _isPeerMediaPermissionRejected: boolean = false;
    @observable public readonly magicMarkerController: MagicMarkerController;
    @observable private _allowMagicMarker: boolean = true;

    @observable private _videoLayoutType: VideoLayoutType = VideoLayoutType.AgentPinnedLayout;
    @observable public onSnapshotRequested?: OnSnapshotRequestedCb = undefined;
    @observable private _canTakeSnapshot = false;

    @observable private _participantToRemove: NwayParticipant | null = null;

    @observable private localVideoDimensions: {width: number; height: number} = {width: 640, height: 480};
    @observable private remoteVideoDimensions: {width: number; height: number} = {width: 640, height: 480};

    constructor(
        private readonly roomData: IRoom,
        private readonly tsChatApi: IChatApi,
        private readonly roomService: IRoomService,
        private readonly eventLog: (eventType: string, meta?: Record<string, unknown>) => Promise<void>,
        private readonly streamEstablishCb: (event?: any) => void,
        private readonly participantContext?: IVideoParticipantContext
    ) {
        super();
        this.magicMarkerController = new MagicMarkerController(
            this.tsChatApi,
            getRootStore().environmentService.isMobile(),
            eventLog
        );
    }

    @computed
    get multipartyController() {
        return this._multipartyController;
    }

    @computed
    get isPeerMediaPermissionRejected() {
        return this._isPeerMediaPermissionRejected;
    }

    @computed
    public get videoLayoutType(): VideoLayoutType {
        return this._videoLayoutType;
    }

    @computed
    public get canTakeSnapshot(): boolean {
        return this._canTakeSnapshot;
    }

    @computed
    public get allowMagicMarker(): boolean {
        return this._allowMagicMarker;
    }

    public onPinnedUserChangeRequested = (pinnedUser: PinningId): void => {
        this.tsChatApi.setStatus('facemeetBigVideoPinnedUser', pinnedUser);

        this.magicMarkerController.clearRemoteDrawings();

        // Enable Magic Marker automatically when video is pinned (agent-only)
        const hasPinnedVideo = pinnedUser && pinnedUser !== 'none';
        this.magicMarkerController.setEnabled(!!hasPinnedVideo);

        this._multipartyController?.setPinnedUser(pinnedUser);

        const pinnedUserRole = this.getPinnedUserRole(pinnedUser);

        // Set mirroring: mirror when viewing YOUR OWN video (not others)
        const shouldMirror =
            pinnedUserRole === this._multipartyController?.userRole && !this._multipartyController?.isScreenSharing;

        this.magicMarkerController.setMirrored(shouldMirror);

        this.eventLog(LOG_EVENTS.facemeetBigVideoPinnedUserChanged);
    };

    private initializeMagicMarker = (): void => {
        this.magicMarkerController.clearRemoteDrawings();
        this.magicMarkerController.setEnabled(true);
    };

    public takeSnapshot = (): string | null => {
        if (!this._canTakeSnapshot) {
            return null;
        }

        const data = this.multipartyController?.takeSnapshot();

        return data ? `data:image/png;base64,${data}` : null;
    };

    @action
    public setMultipartyController = (multipartyController: IMultipartyController): void => {
        this._multipartyController = multipartyController;

        this.initializeHandlers();
        this.logEvents();
        this.initializeMagicMarker();
    };

    @action
    public setIsPeerMediaPermissionsRejected = (mediaPermissionRejected: boolean): void => {
        this._isPeerMediaPermissionRejected = mediaPermissionRejected;
    };

    @computed
    public get nwayParticipants(): NwayParticipant[] {
        return this.participantContext?.nwayParticipants ?? [];
    }

    @computed
    public get isAgent(): boolean {
        return this.participantContext?.isAgent ?? false;
    }

    @action
    public onRemoveParticipantRequested = (userId: string): void => {
        const participant = this.nwayParticipants.find((p) => p.participantId === userId);

        if (participant) {
            this._participantToRemove = participant;
        }
    };

    @computed
    public get canRemoveParticipant(): boolean {
        return this.isAgent && (this.participantContext?.allowRemoveParticipant ?? false);
    }

    @computed
    public get connectedParticipants(): NwayParticipant[] {
        return this.isAgent ? this.nwayParticipants.filter((p) => p.connected) : [];
    }

    @computed
    public get showRemoveModal(): boolean {
        return this._participantToRemove !== null;
    }

    @computed
    public get removeConfirmMessage(): string {
        if (!this._participantToRemove) {
            return '';
        }

        const name = `${this._participantToRemove.firstName} ${this._participantToRemove.lastName}`;

        return this.translate('REACT.NWAY.REMOVE_PARTICIPANT_CONFIRM', {name});
    }

    @computed
    public get confirmBtnText(): string {
        return this.translate('REACT.NWAY.REMOVE_PARTICIPANT_CONFIRM_BTN');
    }

    @computed
    public get cancelBtnText(): string {
        return this.translate('REACT.NWAY.REMOVE_PARTICIPANT_CANCEL_BTN');
    }

    @action
    public confirmRemoveParticipant = (): void => {
        if (this._participantToRemove) {
            const id = this._participantToRemove.participantId;

            this.eventLog(LOG_EVENTS.AgentClickedRemoveParticipant, {participantId: id});

            if (this.isPinnedTargetParticipant(id)) {
                this.onPinnedUserChangeRequested(SessionClientRole.AGENT);
            }

            this.emit(VideoControllerEvents.removeParticipant, id);
        }

        this._participantToRemove = null;
    };

    @action
    public cancelRemoveParticipant = (): void => {
        this._participantToRemove = null;
    };

    @action
    public setVideoLayoutType = (layoutType: VideoLayoutType) => {
        this._videoLayoutType = layoutType;
    };

    private logEvents() {
        this._multipartyController!.on(
            MultipartyEvents.streamPropertyChanged,
            (event: MultipartyStreamPropertyChangedEvent) => {
                this.eventLog(LOG_EVENTS.opentok, event);
            }
        );

        this._multipartyController!.on(MultipartyEvents.toggleAudioMute, (meta: MultipartyAudioStateEvent) => {
            const logEvent = meta.hasAudio ? LOG_EVENTS.audioUnmuted : LOG_EVENTS.audioMuted;
            this.eventLog(logEvent, meta);
        });

        this._multipartyController!.on(MultipartyEvents.connectFailed, (event: MultipartyConnectFailedEvent) => {
            this.eventLog(LOG_EVENTS.opentokError.type, event.meta);
        });

        this._multipartyController!.on(
            MultipartyEvents.connectionCreated,
            (event: MultipartyConnectionCreatedEvent) => {
                this.eventLog(LOG_EVENTS.opentokSessionCreated, event);
            }
        );

        this._multipartyController!.on(
            MultipartyEvents.connectionDestroyed,
            (event: MultipartyConnectionDestroyedEvent) => {
                this.eventLog(LOG_EVENTS.opentok, event);
            }
        );

        this._multipartyController!.on(MultipartyEvents.streamDestroyed, (event: MultipartyStreamDestroyedEvent) => {
            this.eventLog(LOG_EVENTS.opentok, event);
        });

        this._multipartyController!.on(MultipartyEvents.streamCreated, (event: MultipartyStreamCreatedEvent) => {
            this.eventLog(LOG_EVENTS.opentok, event);
        });

        this._multipartyController!.on(MultipartyEvents.mediaDeviceAccessDenied, () => {
            this.eventLog(LOG_EVENTS.opentokError.type);
        });
    }

    private initializeHandlers() {
        this._multipartyController?.on(MultipartyEvents.updateTrafficLogs, (meta: any) => {
            this.updateTrafficLogs(meta.videoTrafficDelta, meta.videoDurationDelta);
        });

        this._multipartyController?.on(
            MultipartyEvents.streamPropertyChanged,
            (e: MultipartyStreamPropertyChangedEvent) =>
                this.updateStreamVideoDimensions(e.isOwnConnection, e.videoDimensions)
        );

        this._multipartyController?.on(MultipartyEvents.streamCreated, (e: MultipartyStreamCreatedEvent) => {
            if (e.isOwnConnection) {
                this.streamEstablishCb();
                if (e.clientId === SessionClientRole.AGENT && !this._multipartyController?.isScreenSharing) {
                    this.magicMarkerController.setMirrored(e.mirror);
                }
            }

            this.updateStreamVideoDimensions(e.isOwnConnection, e.videoDimensions);
        });

        this._multipartyController!.on(
            MultipartyEvents.snapshotAllowedChanged,
            (event: MultipartySnapshotAllowedChangedEvent) => {
                runInAction(() => {
                    this._canTakeSnapshot = event.snapshotAllowed;
                });
            }
        );

        this._multipartyController!.on(
            MultipartyEvents.mainVideoMirrorStateChanged,
            (event: MultipartyMainVideoMirrorStateChangedEvent) => {
                this.magicMarkerController.clearRemoteDrawings();
                this.magicMarkerController.setMirrored(event.mirror);
            }
        );

        this._multipartyController?.on(
            MultipartyEvents.screenShareStateChanged,
            (e: MultipartyScreenShareStateChangedEvent) => {
                // When screen sharing stops, re-enable magic marker and update mirroring
                if (!e.isScreenSharing) {
                    const hasPinnedVideo =
                        this._multipartyController?.pinnedUser && this._multipartyController?.pinnedUser !== 'none';
                    this.magicMarkerController.setEnabled(!!hasPinnedVideo);

                    // Update mirroring based on who's pinned
                    const pinnedUserRole = this.getPinnedUserRole(this._multipartyController?.pinnedUser);
                    const shouldMirror = pinnedUserRole === this._multipartyController?.userRole;
                    this.magicMarkerController.setMirrored(shouldMirror);

                    this.updateMagicMarkerDimensions();
                } else {
                    // During screen sharing, don't mirror
                    this.magicMarkerController.setMirrored(false);

                    this.updateMagicMarkerDimensions();
                }
            }
        );
        this._multipartyController!.on(
            MultipartyEvents.mainVideoPinnedAndStreamingStateChanged,
            (event: MultipartyMainVideoPinnedAndStreamingStateChangedEvent) => {
                runInAction(() => {
                    const pinnedUserRole = this.getPinnedUserRole(this._multipartyController?.pinnedUser);

                    this.magicMarkerController.clearRemoteDrawings();
                    this.magicMarkerController.setEnabled(event.isPinnedAndStreaming);
                    this.magicMarkerController.setMirrored(
                        pinnedUserRole === this._multipartyController?.userRole &&
                            !this._multipartyController?.isScreenSharing
                    );
                    this.updateMagicMarkerDimensions();
                });
            }
        );

        this._multipartyController!.on(MultipartyEvents.mediaDeviceAccessDenied, () => {
            this.streamEstablishCb({mediaDeviceAccessDenied: true});
        });

        this._multipartyController!.on(MultipartyEvents.setPinnedUser, (pinnedUser: PinningId) => {
            const pinnedUserRole = this.getPinnedUserRole(pinnedUser);

            // Set mirroring: mirror when viewing YOUR OWN video (not others)
            const shouldMirror =
                pinnedUserRole === this._multipartyController?.userRole && !this._multipartyController?.isScreenSharing;

            this.magicMarkerController.setMirrored(shouldMirror);
        });
    }

    private getPinnedUserRole(pinnedUser: PinningId | undefined): SessionClientRole {
        if (!pinnedUser || pinnedUser === 'none') {
            return SessionClientRole.OBSERVER;
        }

        // Try finding by userId first (for AGENT/OBSERVER), then by connectionId (for USER)
        let pinnedConnection = this._multipartyController?.connections.find('userId', pinnedUser);
        if (!pinnedConnection) {
            pinnedConnection = this._multipartyController?.connections.find('connectionId', pinnedUser);
        }

        return pinnedConnection ? toSessionClientRoleFromId(pinnedConnection.userId) : SessionClientRole.OBSERVER;
    }

    /** Pinning id may be userId (e.g. observer) or OpenTok connectionId (e.g. USER streams). */
    private isPinnedTargetParticipant(participantId: string): boolean {
        const pinned = this._multipartyController?.pinnedUser;

        if (!pinned || pinned === 'none') {
            return false;
        }

        if (pinned === participantId) {
            return true;
        }

        const conn = this._multipartyController?.connections.find('userId', participantId);

        return !!conn?.connectionId && conn.connectionId === pinned;
    }

    private updateMagicMarkerDimensions(): void {
        const pinnedUserRole = this.getPinnedUserRole(this._multipartyController?.pinnedUser);
        this.magicMarkerController.setVideoDimensions(
            pinnedUserRole === this._multipartyController?.userRole
                ? this.localVideoDimensions
                : this.remoteVideoDimensions
        );
    }

    @action
    private updateStreamVideoDimensions(
        isOwnConnection: boolean,
        videoDimensions: {
            width: number;
            height: number;
        }
    ) {
        if (isOwnConnection) {
            /** @FIXME This state should not be saved here, it should be exposed on multiPartyController */
            this.localVideoDimensions = videoDimensions;
        } else {
            this.remoteVideoDimensions = videoDimensions;
        }

        this.magicMarkerController.clearRemoteDrawings();
    }

    public updateTrafficLogs(videoTrafficDelta: any, videoDurationDelta: any) {
        this.roomService.setReportedField(this.roomData._id, {
            key: 'videoTraffic',
            value: videoTrafficDelta,
            type: 'inc'
        });

        this.roomService.setReportedField(this.roomData._id, {
            key: 'videoDuration',
            value: videoDurationDelta,
            type: 'inc'
        });
    }
}
